import { useState, useCallback } from 'react';
import { api } from '../lib/api';
import type { DashboardStats, SiteStats, DailyStats } from '../types';

export function useAnalytics() {
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [siteStats, setSiteStats] = useState<SiteStats | null>(null);
  const [dailyStats, setDailyStats] = useState<DailyStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getDashboardStats();
      setDashboardStats(data);
      return data;
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Помилка завантаження статистики';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSiteStats = useCallback(async (siteId: string, period: string = '7d') => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getSiteStats(siteId, period);
      setSiteStats(data);
      return data;
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Помилка завантаження статистики сайту';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDailyStats = useCallback(async (siteId: string, days: number = 7) => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getDailyStats(siteId, days);
      setDailyStats(data);
      return data;
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Помилка завантаження щоденної статистики';
      setError(errorMessage);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    dashboardStats,
    siteStats,
    dailyStats,
    loading,
    error,
    fetchDashboardStats,
    fetchSiteStats,
    fetchDailyStats,
  };
}
